import React from 'react';
import { Controller } from 'react-hook-form';
import { TextBoxComponent, NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { type CrudFormProps } from '../../components/CrudPage';
import { type MaterialDto } from '../../api';
import { FormDialog } from '../../components/FormDialog';
import { resolveValidationMessage } from '../../utils/validationMessages';

type MaterialWithId = MaterialDto & { id: string };

// Base unit options: in practice only a few are relevant for intermediate products.
const BASE_UNITS = [
  { value: 'COUNT', text: 'Stück' },
  { value: 'KILOGRAM', text: 'Kilogramm (kg)' },
  { value: 'GRAM', text: 'Gramm (g)' },
  { value: 'LITER', text: 'Liter (l)' },
];

const BASE_UNIT_TO_TYPE: Record<string, string> = {
  COUNT: 'PIECE',
  KILOGRAM: 'MASS',
  GRAM: 'MASS',
  LITER: 'VOLUME',
};

const createSchema = (t: (k: string, options?: { defaultValue: string }) => string) => {
  const requiredMessage = t('validation.required', { defaultValue: 'Dieses Feld ist erforderlich' });
  return z.object({
    name: z.string().min(1, requiredMessage),
    articleNumber: z.string().min(1, requiredMessage),
    description: z.string().optional().nullable(),
    baseUnit: z.string().min(1, requiredMessage),
    global: z.boolean().optional().default(false),

    planningMode: z.enum(['AUTO', 'MANUAL']),
    minLotSize: z.union([z.number().positive(), z.null()]).optional(),
    maxLotSize: z.union([z.number().positive(), z.null()]).optional(),
    lotSizeMultiple: z.union([z.number().positive(), z.null()]).optional(),
    minCoverageDays: z.number().int().min(0),
    leadTimeDays: z.number().int().min(0),
  }).superRefine((data, ctx) => {
    if (data.minLotSize != null && data.maxLotSize != null && data.minLotSize > data.maxLotSize) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('intermediateProduct.lotSizeMinLeMax', { defaultValue: 'Mindestlosgröße darf nicht über Maximallosgröße liegen.' }),
        path: ['maxLotSize'],
      });
    }
  });
};

type IntermediateProductFormData = z.infer<ReturnType<typeof createSchema>>;

const IntermediateProductFormDialog: React.FC<CrudFormProps<MaterialWithId>> = ({
  value,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const schema = React.useMemo(() => createSchema(t as (k: string, options?: { defaultValue: string }) => string), [t]);

  const initialValue: IntermediateProductFormData = {
    name: value?.name ?? '',
    articleNumber: value?.articleNumber ?? '',
    description: value?.description ?? '',
    baseUnit: value?.baseUnit ?? 'KILOGRAM',
    global: value?.global ?? false,
    planningMode: (value?.planningMode as 'AUTO' | 'MANUAL' | undefined) ?? 'AUTO',
    minLotSize: value?.minLotSize ?? null,
    maxLotSize: value?.maxLotSize ?? null,
    lotSizeMultiple: value?.lotSizeMultiple ?? null,
    minCoverageDays: value?.minCoverageDays ?? 1,
    leadTimeDays: value?.leadTimeDays ?? 0,
  };

  return (
    <FormDialog<IntermediateProductFormData>
      value={initialValue}
      onClose={onClose}
      onSave={(data) => {
        // The old product fields are NOT NULL in the backend - on edit pass the existing values through,
        // on create set sensible defaults so that the save does not fail.
        const merged: MaterialWithId = {
          ...(value ?? {}),
          id: value?.id ?? '',
          uuid: value?.uuid,
          tenantId: value?.tenantId,
          tenantName: value?.tenantName,
          materialType: 'INTERMEDIATE_PRODUCT',
          name: data.name,
          articleNumber: data.articleNumber || undefined,
          description: data.description || undefined,
          baseUnit: data.baseUnit,
          baseUnitType: BASE_UNIT_TO_TYPE[data.baseUnit] ?? 'PIECE',
          global: data.global ?? false,
          planningMode: data.planningMode,
          minLotSize: data.minLotSize ?? undefined,
          maxLotSize: data.maxLotSize ?? undefined,
          lotSizeMultiple: data.lotSizeMultiple ?? undefined,
          minCoverageDays: data.minCoverageDays,
          leadTimeDays: data.leadTimeDays,
          minDoughQuantityInKg: value?.minDoughQuantityInKg ?? 0,
          maxDoughQuantityInKg: value?.maxDoughQuantityInKg ?? 250,
          initialWeighInGrams: value?.initialWeighInGrams ?? 0,
          // scrap_rate is NOT NULL in the database (DB default 0), but the entity has no Java default.
          // Without an explicit 0 Hibernate sends NULL and the INSERT fails.
          scrapRate: value?.scrapRate ?? 0,
          defaultOvenTypeUuid: value?.defaultOvenTypeUuid ?? undefined,
          longRunning: value?.longRunning ?? false,
          longTermOffsetDays: value?.longTermOffsetDays ?? undefined,
          piecesPerOven: value?.piecesPerOven ?? 0,
          startTemperature: value?.startTemperature ?? 0,
          endTemperature: value?.endTemperature ?? 0,
          isAlias: false,
          aliasForProductUuid: undefined,
        };
        onSave(merged);
      }}
      titleKey="dialogs.intermediateProduct"
      width="900px"
      resolver={zodResolver(schema) as never}
    >
      {({ control, formState: { errors } }) => (
        <>
          {/* Name + Artikelnummer */}
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('form.name')} *
              </label>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextBoxComponent
                    {...field}
                    placeholder={t('form.name') + ' *'}
                    floatLabelType="Never"
                    cssClass={errors.name ? 'e-error' : ''}
                  />
                )}
              />
              {errors.name && (
                <div className="text-red-500 text-sm mt-1">
                  {resolveValidationMessage(t, errors.name)}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('material.articleNumber', { defaultValue: 'Artikelnummer' })} *
              </label>
              <Controller
                name="articleNumber"
                control={control}
                render={({ field }) => (
                  <TextBoxComponent
                    value={field.value ?? ''}
                    placeholder={t('material.articleNumber', { defaultValue: 'Artikelnummer' })}
                    floatLabelType="Never"
                    change={(e: { value?: string }) => field.onChange(e.value ?? '')}
                    cssClass={errors.articleNumber ? 'e-error' : ''}
                  />
                )}
              />
              {errors.articleNumber && (
                <div className="text-red-500 text-sm mt-1">
                  {resolveValidationMessage(t, errors.articleNumber)}
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('form.description', { defaultValue: 'Beschreibung' })}
            </label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextBoxComponent
                  value={field.value ?? ''}
                  placeholder={t('form.description', { defaultValue: 'Beschreibung' })}
                  floatLabelType="Never"
                  change={(e: { value?: string }) => field.onChange(e.value ?? '')}
                />
              )}
            />
          </div>

          {/* Base unit (planningMode stays in the schema with the default AUTO; the UI is hidden
              until the intermediate product AUTO/MANUAL distinction is reactivated, see
              memory/project_intermediate_simplification_2026-04-29.md) */}
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('form.baseUnit', { defaultValue: 'Basiseinheit' })} *
              </label>
              <Controller
                name="baseUnit"
                control={control}
                render={({ field }) => (
                  <DropDownListComponent
                    dataSource={BASE_UNITS}
                    fields={{ text: 'text', value: 'value' }}
                    placeholder={t('form.baseUnit', { defaultValue: 'Basiseinheit' })}
                    floatLabelType="Never"
                    value={field.value}
                    change={(e: { value?: string }) => field.onChange(e.value ?? '')}
                    cssClass={errors.baseUnit ? 'e-error' : ''}
                  />
                )}
              />
              {errors.baseUnit && (
                <div className="text-red-500 text-sm mt-1">
                  {resolveValidationMessage(t, errors.baseUnit)}
                </div>
              )}
            </div>
            <div />
          </div>

          {/* Min + max lot size */}
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('intermediateProduct.minLotSize', { defaultValue: 'Mindestlosgröße' })}
              </label>
              <Controller
                name="minLotSize"
                control={control}
                render={({ field }) => (
                  <NumericTextBoxComponent
                    format="n3"
                    min={0}
                    value={field.value ?? undefined}
                    change={(e: { value?: number | string | null }) => field.onChange(e.value ?? null)}
                    cssClass={errors.minLotSize ? 'e-error' : ''}
                  />
                )}
              />
              {errors.minLotSize && (
                <div className="text-red-500 text-sm mt-1">
                  {resolveValidationMessage(t, errors.minLotSize)}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('intermediateProduct.maxLotSize', { defaultValue: 'Maximallosgröße' })}
              </label>
              <Controller
                name="maxLotSize"
                control={control}
                render={({ field }) => (
                  <NumericTextBoxComponent
                    format="n3"
                    min={0}
                    value={field.value ?? undefined}
                    change={(e: { value?: number | string | null }) => field.onChange(e.value ?? null)}
                    cssClass={errors.maxLotSize ? 'e-error' : ''}
                  />
                )}
              />
              {errors.maxLotSize && (
                <div className="text-red-500 text-sm mt-1">
                  {resolveValidationMessage(t, errors.maxLotSize)}
                </div>
              )}
            </div>
          </div>

          {/* Step size (on its own, the right slot stays free for a calm 2-column layout) */}
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('intermediateProduct.lotSizeMultiple', { defaultValue: 'Schrittweite' })}
              </label>
              <Controller
                name="lotSizeMultiple"
                control={control}
                render={({ field }) => (
                  <NumericTextBoxComponent
                    format="n3"
                    min={0}
                    value={field.value ?? undefined}
                    change={(e: { value?: number | string | null }) => field.onChange(e.value ?? null)}
                  />
                )}
              />
            </div>
            <div />
          </div>

          {/* leadTimeDays + minCoverageDays hidden in the UI (defaults 0 / 1 from the schema);
              for reactivation see memory/project_intermediate_simplification_2026-04-29.md */}

          {/* Global as the last field */}
          <div className="mb-2">
            <Controller
              name="global"
              control={control}
              render={({ field }) => (
                <CheckBoxComponent
                  label={t('form.global', { defaultValue: 'Global (für alle Planungsbereiche sichtbar)' })}
                  checked={field.value ?? false}
                  change={(e: { checked?: boolean }) => field.onChange(e.checked ?? false)}
                />
              )}
            />
          </div>
        </>
      )}
    </FormDialog>
  );
};

export default IntermediateProductFormDialog;
